import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Checkbox, Fieldset, Input, Layout, OverlayPopover, Pill } from '@utilities';

import { VALIDATION_MESSAGES } from '../../utilities/constants';

import styles from './_index.module.scss';

const MAX_TOTAL_MAX = 9999999;

const MaxTotal = ({ question, setQuestion }) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [propertiesToApply, setPropertiesToApply] = useState({ ...question.properties });

  const pillRef = useRef();
  const popoverRef = useRef(); // required to allow inputs inside the popover to be focused

  useEffect(() => {
    if (isPopoverOpen) {
      setPropertiesToApply({ ...question.properties });
    }
  }, [isPopoverOpen]);

  const handleApply = useCallback(() => {
    setQuestion((prev) => ({
      ...prev,
      properties: { ...prev.properties, ...propertiesToApply },
    }));
    setIsPopoverOpen(false);
  }, [propertiesToApply, setQuestion]);

  const isApplyDisabled =
    isNaN(propertiesToApply.maxTotal) ||
    !Number.isInteger(propertiesToApply.maxTotal) ||
    propertiesToApply.maxTotal > MAX_TOTAL_MAX ||
    propertiesToApply.maxTotal < question.properties.maxNumber ||
    (question.properties.forcePercent && propertiesToApply.maxTotal > 100) ||
    (question.properties.maxTotal === propertiesToApply.maxTotal &&
      question.properties.mustBeMax === propertiesToApply.mustBeMax);

  const getErrorMessage = () => {
    if (propertiesToApply.maxTotal > MAX_TOTAL_MAX) {
      return 'Max total cannot exceed 9,999,999.';
    }
    if (!Number.isInteger(propertiesToApply.maxTotal) || propertiesToApply.maxTotal <= 0) {
      return 'Max total must be a positive whole number.';
    }
    if (propertiesToApply.maxTotal < question.properties.maxNumber) {
      return 'Max total must be greater than or equal to the maximum allowed value.';
    }
    if (question.properties.forcePercent && propertiesToApply.maxTotal > 100)
      return VALIDATION_MESSAGES.MAX_PERCENTAGE_ERROR;
    return undefined;
  };

  return (
    <>
      <Layout.Flex className={styles['max-total']} gap="x-small">
        <span ref={popoverRef}>Max Total</span>
        <Pill
          buttonRef={pillRef}
          data-testid="max-total-pill"
          isModified={!!question.properties?.maxAnswers}
          onClick={() => setIsPopoverOpen(!isPopoverOpen)}
          value={question.properties.maxTotal}
        />
      </Layout.Flex>
      <OverlayPopover
        className={styles['max-total-popover']}
        container={popoverRef.current}
        data-testid="max-total-popover"
        hasArrow
        isActive={isPopoverOpen}
        onHide={() => setIsPopoverOpen(false)}
        target={pillRef.current}
      >
        <>
          <Input
            className={styles['max-total-input']}
            error={getErrorMessage()}
            id="max-total-input"
            label="Max Total"
            max={MAX_TOTAL_MAX}
            min={0}
            onChange={(value) =>
              setPropertiesToApply((prev) => ({ ...prev, maxTotal: parseFloat(value) }))
            }
            type="number"
            value={propertiesToApply.maxTotal}
          />
          <hr />
          <Fieldset legend="Additional Options">
            <Checkbox
              className={styles['max-total-checkbox']}
              isChecked={propertiesToApply.mustBeMax}
              label="Answers must equal this number"
              onChange={() =>
                setPropertiesToApply((prev) => ({
                  ...prev,
                  mustBeMax: !prev.mustBeMax,
                }))
              }
              value={propertiesToApply.mustBeMax}
            />
          </Fieldset>
          <Button isDisabled={isApplyDisabled} onClick={handleApply} text="Apply" />
        </>
      </OverlayPopover>
    </>
  );
};

MaxTotal.propTypes = {
  question: PropTypes.object.isRequired,
  setQuestion: PropTypes.func.isRequired,
};

export default MaxTotal;
